import { ICON_PROPS, postToHost } from './shared.js';
import { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  ActionIcon,
  Alert,
  AppShell,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  Chip,
  Code,
  Collapse,
  Container,
  Divider,
  Drawer,
  Grid,
  Group,
  List,
  Loader,
  Menu,
  Modal,
  MultiSelect,
  NativeSelect,
  NavLink,
  NumberInput,
  Paper,
  PasswordInput,
  Progress,
  RingProgress,
  ScrollArea,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
  Tooltip,
  useMantineColorScheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArchive,
  IconArrowBackUp,
  IconBell,
  IconBolt,
  IconBrain,
  IconBriefcase,
  IconCalendar,
  IconChartBar,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconClock,
  IconCommand,
  IconDeviceMobile,
  IconDownload,
  IconEdit,
  IconFileText,
  IconFlag,
  IconFolder,
  IconGavel,
  IconHistory,
  IconMail,
  IconMailOff,
  IconMessageQuestion,
  IconMoonStars,
  IconNews,
  IconPaperclip,
  IconPencil,
  IconPhone,
  IconPlus,
  IconRadar,
  IconReload,
  IconReport,
  IconSchool,
  IconSearch,
  IconSend,
  IconSettings,
  IconShare,
  IconShield,
  IconSparkles,
  IconStar,
  IconStarFilled,
  IconTarget,
  IconTrash,
  IconUser,
  IconUserPlus,
  IconUsersGroup,
  IconWand,
  IconX,
} from '@tabler/icons-react';
import { api, getStoredToken, getStoredUser } from '../../api.js';
import { type IndexState } from './shared.js';
// Round 48 follow-up (2026-07-15): AddinPage.tsx monolith split — settings panel + trust center cards,
// moved VERBATIM. Layering: shared ← shared-components ← panels ← AddinPage.

export function SettingsPanel({
  state,
  onSignOut,
  addinVersion,
}: {
  state: IndexState | null;
  onSignOut: () => void;
  addinVersion: string | null;
}): React.JSX.Element {
  const u = getStoredUser();
  const [tone, setTone] = useState<string>('balanced');
  const { colorScheme, setColorScheme } = useMantineColorScheme();

  return (
    <Stack gap="sm">
      <Card padding="md" withBorder>
        <Stack gap="xs">
          <Title order={5}>Appearance</Title>
          <SegmentedControl
            value={colorScheme}
            onChange={(v) => setColorScheme(v as 'light' | 'dark' | 'auto')}
            data={[
              { value: 'light', label: 'Light' },
              { value: 'dark', label: 'Dark' },
              { value: 'auto', label: 'Auto' },
            ]}
          />
        </Stack>
      </Card>

      <Card padding="md" withBorder>
        <Stack gap="xs">
          <Title order={5}>Account</Title>
          <Text size="sm">
            Signed in as <b>{u?.email}</b>
          </Text>
          <Text size="xs" c="dimmed">
            Role: {u?.global_role}
          </Text>
          <Group>
            <Button size="xs" variant="default" color="red" onClick={onSignOut}>
              Sign out
            </Button>
          </Group>
        </Stack>
      </Card>

      {/* Round 29 / v3.5.56: user-scoped IMAP account setup. Brain
          connects directly via imapflow IDLE → real-time new mail without
          Outlook involvement. Eliminates the 10-min auto-index storm. */}
      <Card padding="md" withBorder>
        <Stack gap="xs">
          <Title order={5}>Email accounts (IMAP)</Title>
          <Text size="sm" c="dimmed">
            Connect your email server directly to ai-outlook so new mail is
            captured in real-time, even when Outlook is closed. This is what
            makes search and AI faster than Outlook itself.
          </Text>
          <Text size="xs" c="dimmed">
            cPanel users typically have: server <Code>mail.&lt;your-domain&gt;</Code>,
            port <Code>993</Code>, SSL on, username = your email address.
          </Text>
          <Group>
            <Button
              size="xs"
              variant="filled"
              color="violet"
              leftSection={<IconMail size={14} />}
              onClick={() => window.open('/accounts/new', '_blank', 'noopener')}
            >
              Add IMAP account
            </Button>
            <Button
              size="xs"
              variant="default"
              onClick={() => window.open('/accounts', '_blank', 'noopener')}
            >
              Manage accounts
            </Button>
          </Group>
        </Stack>
      </Card>

      <Card padding="md" withBorder>
        <Stack gap="xs">
          <Title order={5}>Reply tone</Title>
          <Text size="sm" c="dimmed">
            Default tone for AI-drafted replies. (Coming online once the brain is wired to a real
            LLM key — currently mock.)
          </Text>
          <SegmentedControl
            value={tone}
            onChange={setTone}
            data={[
              { value: 'formal', label: 'Formal' },
              { value: 'balanced', label: 'Balanced' },
              { value: 'casual', label: 'Casual' },
              { value: 'stern', label: 'Stern' },
            ]}
          />
        </Stack>
      </Card>

      <Card padding="md" withBorder>
        <Stack gap="xs">
          <Title order={5}>Indexing</Title>
          <Text size="xs" c="dimmed">
            Auto-index every {state?.auto_index_minutes ?? 0} min ·{' '}
            live {state?.new_mail_hooked ? 'on' : 'off'}
          </Text>
          <Text size="xs" c="dimmed">
            Change these in the Index tab.
          </Text>
        </Stack>
      </Card>

      <Card padding="md" withBorder>
        <Stack gap="xs">
          <Title order={5}>Versions</Title>
          <Group justify="space-between" gap="xs">
            <Text size="sm" c="dimmed">Outlook addin (COM)</Text>
            <Text size="sm" ff="monospace">
              {addinVersion ?? '(not detected — running outside the addin)'}
            </Text>
          </Group>
          <Group justify="space-between" gap="xs">
            <Text size="sm" c="dimmed">Brain endpoint</Text>
            <Text size="sm" ff="monospace">{window.location.origin}</Text>
          </Group>
          {/* Round 36 follow-up: manual update-check trigger. The addin
              polls /addin/release hourly + 5s after WebView ready, but
              if a release lands mid-hour the user has to wait or close
              and reopen the pane. This button posts check_for_update_now
              to the host, which fires CheckForUpdateAsync immediately. */}
          {addinVersion && (
            <Group justify="flex-end" mt={4}>
              <Button
                size="xs"
                variant="default"
                onClick={() => {
                  postToHost({ type: 'check_for_update_now' });
                  notifications.show({
                    color: 'blue',
                    message: 'Checking for updates… if available, the modal will appear in a few seconds.',
                  });
                }}
              >
                Check for updates
              </Button>
            </Group>
          )}
        </Stack>
      </Card>

      <EmailAliasesCard />
      <PrivateModeCard />
      <ReplyBriefAutoOpenToggle />
      {/* Tier 1 #3 — AI category definitions (natural-language, fed to classifier) */}
      <CategoryDefinitionsCard />
      {/* Phase 8 — Trust Center */}
      <TrustCenterCard />
    </Stack>
  );
}

// =========================================================================
// AI category definitions editor.
//
// The classifier (Claude Haiku via airouter) reads each email and tags it
// with categories: legal / finance / urgency / contract / etc. What each
// category MEANS is described here in natural language and gets injected
// into the classifier system prompt — so the LLM does semantic matching,
// not regex. Want "anything from galadarilaw.com is legal even when it's
// a one-line 'no update' email"? Type it. The LLM understands.
// =========================================================================
export const CATEGORY_LABELS: Record<string, string> = {
  legal: 'Legal',
  dispute: 'Dispute',
  operator: 'Operator / telecom',
  regulator: 'Regulator',
  contract: 'Contract',
  finance: 'Finance',
  urgency: 'Urgency',
  bigclient: 'Big client / executive',
};

export function CategoryDefinitionsCard(): React.JSX.Element {
  const [data, setData] = useState<{
    definitions: Record<string, string>;
    defaults: Record<string, string>;
    customized: boolean;
  } | null>(null);
  const [draft, setDraft] = useState<Record<string, string> | null>(null);
  const [pending, setPending] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api.addinCategoryDefs();
      setData(d);
      setDraft({ ...d.definitions });
    } catch (e) {
      notifications.show({ color: 'red', message: `Could not load category definitions: ${String(e)}` });
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  if (!data || !draft) return <Card withBorder><Loader size="sm" /></Card>;

  const dirty = JSON.stringify(draft) !== JSON.stringify(data.definitions);

  const onSave = async () => {
    setPending(true);
    try {
      await api.addinCategoryDefsUpdate(draft);
      notifications.show({ color: 'teal', message: 'Category definitions saved. New emails will be classified using the updated definitions.' });
      await load();
    } catch (e) {
      notifications.show({ color: 'red', message: `Save failed: ${String(e)}` });
    } finally {
      setPending(false);
    }
  };
  const onReset = async () => {
    if (!confirm('Reset all category definitions to defaults? Your custom descriptions will be removed.')) return;
    setPending(true);
    try {
      await api.addinCategoryDefsReset();
      notifications.show({ color: 'teal', message: 'Reset to defaults.' });
      await load();
    } catch (e) {
      notifications.show({ color: 'red', message: `Reset failed: ${String(e)}` });
    } finally {
      setPending(false);
    }
  };

  return (
    <Card withBorder>
      <Stack gap="sm">
        <Group justify="space-between">
          <Box>
            <Title order={5}>AI category definitions</Title>
            <Text size="xs" c="dimmed">
              Tell the AI what each category means for YOUR business — in plain English (or Arabic).
              The classifier reads each email and tags it with categories matching these descriptions.
              No regex, no keyword lists — semantic matching.
            </Text>
          </Box>
          {data.customized && <Badge size="xs" color="grape">Customized</Badge>}
        </Group>
        <Alert color="blue" variant="light" p="xs">
          <Text size="xs">
            Example: "Legal = anything from galadarilaw.com, mentions of DIAC arbitration, court orders.
            Even a one-line 'no update' from our law firm IS legal." The AI then tags every Galadari email
            as legal regardless of wording.
          </Text>
        </Alert>
        {Object.entries(CATEGORY_LABELS).map(([cat, label]) => {
          const isCustomized = draft[cat] !== data.defaults[cat];
          return (
            <Box key={cat}>
              <Group justify="space-between" mb={2}>
                <Text size="sm" fw={500}>{label}</Text>
                {isCustomized && <Badge size="xs" color="grape" variant="light">edited</Badge>}
              </Group>
              <TextInput
                size="xs"
                value={draft[cat] ?? ''}
                onChange={(e) => setDraft({ ...draft, [cat]: e.currentTarget.value })}
                placeholder={data.defaults[cat] ?? ''}
                styles={{ input: { fontSize: 12, lineHeight: 1.4 } }}
              />
            </Box>
          );
        })}
        <Group justify="flex-end" gap="xs" mt="xs">
          <Button size="xs" variant="subtle" color="gray" onClick={onReset} loading={pending}>
            Reset to defaults
          </Button>
          <Button size="xs" onClick={onSave} disabled={!dirty} loading={pending}>
            Save definitions
          </Button>
        </Group>
        <Text size="xs" c="dimmed">
          Changes apply to NEW classifications. Already-classified emails keep their tags until re-processed.
        </Text>
      </Stack>
    </Card>
  );
}

// =========================================================================
// Per-user signal-pattern editor — promote + demote keyword lists that
// drive the Action Inbox classifier and Cases-suggest. Each category is
// shown as a chip-list; user can add/remove keywords; "Reset to defaults"
// drops their overrides. Patterns are stored in addin_settings and merged
// with the curated defaults at request time. Empty list = disable a
// category. Roles/CEO already bias which categories matter; this lets
// you tune the *what counts as* legal/finance/etc. for your company.
// =========================================================================
export const PROMOTE_LABELS: Record<string, { label: string; hint: string }> = {
  legal: { label: 'Legal', hint: 'Lawsuits, tribunals, arbitration, court, counsel — anything from your law firm.' },
  dispute: { label: 'Dispute', hint: 'Complaints, refund demands, escalations, formal grievances.' },
  operator: { label: 'Operator / telecom', hint: 'Carrier interconnect, MVNO/MNO traffic, porting, routing.' },
  regulator: { label: 'Regulator', hint: 'TRA / FCC / Ofcom / GDPR / regulatory orders + findings.' },
  contract: { label: 'Contract', hint: 'MSA / NDA / SOW / amendments / signature requests / RFPs.' },
  finance: { label: 'Finance', hint: 'Overdue invoices, wire transfers, IBAN, audit, tax notices.' },
  urgency: { label: 'Urgency', hint: 'Urgent / ASAP / EOD / outage / production issue.' },
  bigclient: { label: 'Big client / executive', hint: 'CEO / CFO / VP / board / C-level escalations.' },
};

export function SignalPatternsCard(): React.JSX.Element {
  const [data, setData] = useState<{
    patterns: { promote: Record<string, string[]>; demote: string[] };
    defaults: { promote: Record<string, string[]>; demote: string[] };
    customized: boolean;
  } | null>(null);
  const [pending, setPending] = useState(false);
  const [draft, setDraft] = useState<{ promote: Record<string, string[]>; demote: string[] } | null>(null);
  const [newWord, setNewWord] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    try {
      const d = await api.addinPatterns();
      setData(d);
      setDraft({
        promote: { ...d.patterns.promote },
        demote: [...d.patterns.demote],
      });
    } catch (e) {
      notifications.show({ color: 'red', message: `Could not load patterns: ${String(e)}` });
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (!data || !draft) return <Card withBorder><Loader size="sm" /></Card>;

  const dirty = JSON.stringify(draft) !== JSON.stringify(data.patterns);

  const onSave = async () => {
    setPending(true);
    try {
      await api.addinPatternsUpdate(draft);
      notifications.show({ color: 'teal', message: 'Patterns saved.' });
      await load();
    } catch (e) {
      notifications.show({ color: 'red', message: `Save failed: ${String(e)}` });
    } finally {
      setPending(false);
    }
  };

  const onReset = async () => {
    if (!confirm('Reset to curated defaults? Your custom keywords will be removed.')) return;
    setPending(true);
    try {
      await api.addinPatternsReset();
      notifications.show({ color: 'teal', message: 'Reset to defaults.' });
      await load();
    } catch (e) {
      notifications.show({ color: 'red', message: `Reset failed: ${String(e)}` });
    } finally {
      setPending(false);
    }
  };

  const removeWord = (cat: 'demote' | string, w: string) => {
    if (cat === 'demote') {
      setDraft({ ...draft, demote: draft.demote.filter((x) => x !== w) });
    } else {
      setDraft({
        ...draft,
        promote: { ...draft.promote, [cat]: (draft.promote[cat] ?? []).filter((x) => x !== w) },
      });
    }
  };
  const addWord = (cat: 'demote' | string) => {
    const w = (newWord[cat] ?? '').trim().toLowerCase();
    if (!w) return;
    if (cat === 'demote') {
      if (draft.demote.includes(w)) return;
      setDraft({ ...draft, demote: [...draft.demote, w] });
    } else {
      const cur = draft.promote[cat] ?? [];
      if (cur.includes(w)) return;
      setDraft({ ...draft, promote: { ...draft.promote, [cat]: [...cur, w] } });
    }
    setNewWord({ ...newWord, [cat]: '' });
  };

  return (
    <Card withBorder>
      <Stack gap="sm">
        <Group justify="space-between">
          <Title order={5}>Signal patterns</Title>
          {data.customized && <Badge size="xs" color="grape">Customized</Badge>}
        </Group>
        <Text size="sm" c="dimmed">
          Keywords that mark an email as ★ Legal / Finance / Urgency etc. in Action Inbox + Cases.
          Add words specific to your business (e.g. client/court names, internal product codes).
          Empty list = disable that category. Edits apply on the next refresh of Action Inbox.
        </Text>
        {Object.entries(PROMOTE_LABELS).map(([cat, meta]) => (
          <Card key={cat} withBorder p="xs" radius="sm">
            <Group justify="space-between" mb={4}>
              <Box>
                <Text size="sm" fw={500}>{meta.label}</Text>
                <Text size="xs" c="dimmed">{meta.hint}</Text>
              </Box>
              <Badge size="xs" variant="light">{(draft.promote[cat] ?? []).length} keywords</Badge>
            </Group>
            <Group gap={4} wrap="wrap">
              {(draft.promote[cat] ?? []).map((w) => (
                <Badge
                  key={w} size="sm" variant="light" color="grape"
                  rightSection={
                    <ActionIcon size="xs" variant="transparent" color="grape" onClick={() => removeWord(cat, w)} aria-label={`remove ${w}`}>
                      ×
                    </ActionIcon>
                  }
                >
                  {w}
                </Badge>
              ))}
            </Group>
            <Group gap={4} mt={4}>
              <TextInput
                size="xs"
                placeholder="add keyword…"
                value={newWord[cat] ?? ''}
                onChange={(e) => setNewWord({ ...newWord, [cat]: e.currentTarget.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addWord(cat); } }}
                style={{ flex: 1 }}
              />
              <Button size="xs" variant="light" onClick={() => addWord(cat)} disabled={!(newWord[cat] ?? '').trim()}>
                Add
              </Button>
            </Group>
          </Card>
        ))}
        <Card withBorder p="xs" radius="sm">
          <Text size="sm" fw={500} mb={2}>Demote (noise) keywords</Text>
          <Text size="xs" c="dimmed" mb={4}>
            Subjects matching any of these are pushed to KNOW / IGNORE — newsletters, alerts, automated reports, rate sheets, etc.
          </Text>
          <Group gap={4} wrap="wrap">
            {draft.demote.map((w) => (
              <Badge
                key={w} size="sm" variant="light" color="gray"
                rightSection={
                  <ActionIcon size="xs" variant="transparent" color="gray" onClick={() => removeWord('demote', w)} aria-label={`remove ${w}`}>
                    ×
                  </ActionIcon>
                }
              >
                {w}
              </Badge>
            ))}
          </Group>
          <Group gap={4} mt={4}>
            <TextInput
              size="xs"
              placeholder="add demote keyword…"
              value={newWord.demote ?? ''}
              onChange={(e) => setNewWord({ ...newWord, demote: e.currentTarget.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addWord('demote'); } }}
              style={{ flex: 1 }}
            />
            <Button size="xs" variant="light" onClick={() => addWord('demote')} disabled={!(newWord.demote ?? '').trim()}>
              Add
            </Button>
          </Group>
        </Card>
        <Group justify="flex-end" gap="xs">
          <Button size="xs" variant="subtle" color="gray" onClick={onReset} loading={pending}>
            Reset to defaults
          </Button>
          <Button size="xs" onClick={onSave} disabled={!dirty} loading={pending}>
            Save patterns
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

// =========================================================================
// Phase 8 — Trust Center (Round 55): provider routing matrix, sensitive
// folder exclusions, audit export, retention controls.
// =========================================================================

export function ReplyBriefAutoOpenToggle(): React.JSX.Element {
  const [enabled, setEnabled] = useState<boolean>(() => {
    try { return localStorage.getItem('addin_brief_auto_open') === '1'; } catch { return false; }
  });
  return (
    <Card padding="md" withBorder>
      <Stack gap="xs">
        <Title order={5}>Reply brief preferences</Title>
        <Switch
          checked={enabled}
          label="Auto-open Brief when composer opens"
          description="When on, the brief panel surfaces below the Reply button by default."
          onChange={(e) => {
            const v = e.currentTarget.checked;
            setEnabled(v);
            localStorage.setItem('addin_brief_auto_open', v ? '1' : '0');
          }}
        />
      </Stack>
    </Card>
  );
}

export function TrustCenterCard(): React.JSX.Element {
  type SettingsShape = Awaited<ReturnType<typeof api.addinSettingsGet>>;
  const [settings, setSettings] = useState<SettingsShape | null>(null);
  const [retention, setRetention] = useState<Awaited<ReturnType<typeof api.addinRetentionPreview>> | null>(null);
  const [excludeText, setExcludeText] = useState('');
  const [routing, setRouting] = useState<Record<string, 'private' | 'cloud'>>({});
  const [saving, setSaving] = useState(false);
  const [sweeping, setSweeping] = useState(false);
  const [days, setDays] = useState({ messages: 0, attachments: 0, audit: 0, security: 0 });

  const ROLES: Array<{ key: string; label: string; hint: string }> = [
    { key: 'draft_model',          label: 'Reply drafting',     hint: 'Cloud → better drafts. Private → never leaves your VPS.' },
    { key: 'bulk_classifier',      label: 'Bulk classification', hint: 'Tier assignment for incoming mail.' },
    { key: 'digest_model',         label: 'Daily digest',        hint: 'Morning brief generation.' },
    { key: 'executive_risk_model', label: 'Executive Risk',      hint: 'Bank-detail / legal / contract analysis.' },
    { key: 'smart_search_rewrite', label: 'Search rewrite',      hint: 'Natural-language → SQL/FTS query parser.' },
    { key: 'embeddings',           label: 'Embeddings',          hint: 'Vector index for semantic search.' },
  ];

  async function load() {
    try {
      const s = await api.addinSettingsGet();
      setSettings(s);
      setExcludeText((s.excluded_folders ?? []).join('\n'));
      setRouting((s.routing_overrides ?? {}) as Record<string, 'private' | 'cloud'>);
      setDays({
        messages: s.retention_days_messages ?? 0,
        attachments: s.retention_days_attachments ?? 0,
        audit: s.retention_days_audit ?? 0,
        security: s.retention_days_security ?? 0,
      });
    } catch { /* silent */ }
    try { setRetention(await api.addinRetentionPreview()); } catch { /* silent */ }
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: load on mount
  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true);
    try {
      const folders = excludeText.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
      await api.addinSettingsSet({
        excluded_folders: folders,
        routing_overrides: routing,
        retention_days_messages: days.messages,
        retention_days_attachments: days.attachments,
        retention_days_audit: days.audit,
        retention_days_security: days.security,
      });
      notifications.show({ color: 'green', message: 'Trust Center settings saved.' });
      load();
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Save failed' });
    } finally { setSaving(false); }
  }

  async function sweep() {
    if (!confirm('Run retention sweep now? This deletes data older than your cutoffs and is irreversible.')) return;
    setSweeping(true);
    try {
      const r = await api.addinRetentionSweep();
      const total = r.deleted.messages + r.deleted.attachments + r.deleted.audit + r.deleted.security;
      notifications.show({ color: 'orange', message:
        `Sweep complete — deleted ${total} row${total === 1 ? '' : 's'} (msg ${r.deleted.messages}, att ${r.deleted.attachments}, audit ${r.deleted.audit}, scans ${r.deleted.security})` });
      load();
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Sweep failed' });
    } finally { setSweeping(false); }
  }

  function downloadAudit(format: 'csv' | 'json') {
    const url = api.addinAuditExportUrl({ format });
    const tok = getStoredToken();
    if (!tok) { notifications.show({ color: 'red', message: 'Not authenticated' }); return; }
    // Fetch + trigger download (raw <a href> wouldn't carry the bearer header).
    fetch(url, { headers: { authorization: `Bearer ${tok}` } })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objUrl;
        a.download = `audit-${new Date().toISOString().slice(0, 10)}.${format}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objUrl);
      })
      .catch((e) => notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Download failed' }));
  }

  return (
    <Card padding="md" withBorder
      style={{ borderLeft: '4px solid var(--mantine-color-teal-6)' }}>
      <Stack gap="md">
        <Group gap="xs">
          <IconShield {...ICON_PROPS} />
          <Title order={5}>Trust Center</Title>
          <Badge size="xs" variant="light" color="teal">Phase 8</Badge>
        </Group>
        <Text size="xs" c="dimmed">
          Decide what AI sees, where it runs, what's logged, and how long any of it sticks around.
        </Text>

        {/* Quick presets — one click sets the routing matrix to a sensible default. */}
        <Group gap="xs" wrap="wrap">
          <Text size="xs" c="dimmed">Quick presets:</Text>
          <Button size="compact-xs" variant="light" color="gray" onClick={() => {
            const all = ['draft_model', 'bulk_classifier', 'digest_model', 'executive_risk_model', 'smart_search_rewrite', 'embeddings'] as const;
            const next: Record<string, 'private' | 'cloud'> = {};
            for (const r of all) next[r] = 'private';
            setRouting(next);
            notifications.show({ message: 'Maximum privacy: all roles → private. Save to apply.' });
          }}>
            🔒 Max privacy
          </Button>
          <Button size="compact-xs" variant="light" color="blue" onClick={() => {
            // Sensitive roles private; bulk routing roles auto.
            const next: Record<string, 'private' | 'cloud'> = {
              executive_risk_model: 'private',
              draft_model: 'private',
            };
            setRouting(next);
            notifications.show({ message: 'Balanced: drafting + executive risk → private; rest → auto.' });
          }}>
            ⚖ Balanced
          </Button>
          <Button size="compact-xs" variant="light" color="indigo" onClick={() => {
            const all = ['draft_model', 'bulk_classifier', 'digest_model', 'executive_risk_model', 'smart_search_rewrite', 'embeddings'] as const;
            const next: Record<string, 'private' | 'cloud'> = {};
            for (const r of all) next[r] = 'cloud';
            setRouting(next);
            notifications.show({ message: 'Cloud-first: all roles → cloud. Save to apply.' });
          }}>
            ☁ Cloud-first
          </Button>
          <Button size="compact-xs" variant="subtle" color="gray" onClick={() => {
            setRouting({});
            notifications.show({ message: 'Cleared overrides. All roles → auto.' });
          }}>
            Clear overrides
          </Button>
        </Group>

        {/* 1. Provider routing matrix */}
        <Card padding="sm" radius="sm" withBorder>
          <Stack gap="xs">
            <Group gap="xs">
              <IconBolt size={14} />
              <Text fw={600} size="sm">Provider routing</Text>
            </Group>
            <Text size="xs" c="dimmed">
              Force a role to private (Ollama) regardless of global settings, or pin it to cloud.
              Saved here today; runtime enforcement lands in a follow-up.
            </Text>
            {ROLES.map((r) => (
              <Group key={r.key} justify="space-between" wrap="nowrap">
                <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={500}>{r.label}</Text>
                  <Text size="xs" c="dimmed" lineClamp={1}>{r.hint}</Text>
                </Stack>
                <SegmentedControl
                  size="xs"
                  value={routing[r.key] ?? 'auto'}
                  onChange={(v) => {
                    setRouting((prev) => {
                      const next = { ...prev };
                      if (v === 'auto') delete next[r.key];
                      else next[r.key] = v as 'private' | 'cloud';
                      return next;
                    });
                  }}
                  data={[
                    { value: 'auto', label: 'Auto' },
                    { value: 'cloud', label: 'Cloud' },
                    { value: 'private', label: 'Private' },
                  ]}
                />
              </Group>
            ))}
          </Stack>
        </Card>

        {/* 2. Sensitive folder exclusions */}
        <Card padding="sm" radius="sm" withBorder>
          <Stack gap="xs">
            <Group gap="xs">
              <IconFolder size={14} />
              <Text fw={600} size="sm">Sensitive folder exclusions</Text>
            </Group>
            <Text size="xs" c="dimmed">
              One folder path per line. Mail under any listed folder is forced to private mode and
              kept out of cloud LLM prompts. Match is exact prefix on Outlook folder path
              (e.g. <code>Inbox/HR</code>, <code>Legal/Contracts</code>).
            </Text>
            <Textarea
              autosize minRows={3} maxRows={10}
              placeholder={'Inbox/HR\nLegal/Contracts\nFinance/Bank statements'}
              value={excludeText}
              onChange={(e) => setExcludeText(e.currentTarget.value)}
              styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
            />
          </Stack>
        </Card>

        {/* 3. Retention controls */}
        <Card padding="sm" radius="sm" withBorder>
          <Stack gap="xs">
            <Group gap="xs">
              <IconClock size={14} />
              <Text fw={600} size="sm">Retention</Text>
              <Text size="xs" c="dimmed">0 = keep forever</Text>
            </Group>
            <SimpleGrid cols={2} spacing="xs">
              {(['messages', 'attachments', 'audit', 'security'] as const).map((k) => (
                <Stack key={k} gap={2}>
                  <Text size="xs" fw={500} tt="capitalize">{k} (days)</Text>
                  <NumberInput
                    size="xs"
                    min={0} max={36500}
                    value={days[k]}
                    onChange={(v) => setDays((prev) => ({ ...prev, [k]: typeof v === 'number' ? v : 0 }))}
                  />
                  {retention?.plan?.[k]?.cutoff && (
                    <Text size="xs" c="dimmed">
                      Would delete {retention.plan[k].would_delete.toLocaleString()} row(s) older than {new Date(retention.plan[k].cutoff!).toLocaleDateString()}
                    </Text>
                  )}
                </Stack>
              ))}
            </SimpleGrid>
            <Group gap="xs">
              <Button size="xs" variant="default" onClick={() => api.addinRetentionPreview().then(setRetention).catch(() => {})}>
                Refresh preview
              </Button>
              <Button size="xs" color="orange" loading={sweeping} onClick={sweep}
                disabled={!retention || (
                  retention.plan.messages.would_delete +
                  retention.plan.attachments.would_delete +
                  retention.plan.audit.would_delete +
                  retention.plan.security.would_delete
                ) === 0}>
                Run sweep now
              </Button>
            </Group>
          </Stack>
        </Card>

        {/* Export everything — bundle major data types into one JSON */}
        <Card padding="sm" radius="sm" withBorder>
          <Stack gap="xs">
            <Group gap="xs">
              <IconFileText size={14} />
              <Text fw={600} size="sm">Export everything</Text>
            </Group>
            <Text size="xs" c="dimmed">
              One file: cases + promises + decisions + last 100 audit entries + saved search presets.
            </Text>
            <Button size="xs" variant="default" leftSection={<IconFileText size={12} />}
              onClick={async () => {
                try {
                  const [cases, promises, decisions, audit] = await Promise.all([
                    api.addinCasesList().catch(() => ({ cases: [] })),
                    api.addinPromisesList('pending').catch(() => ({ promises: [] })),
                    api.addinDecisionsList().catch(() => ({ decisions: [] })),
                    api.addinAuditLog(100).catch(() => ({ entries: [] })),
                  ]);
                  let presets: unknown = [];
                  try { presets = JSON.parse(localStorage.getItem('addin_search_presets') ?? '[]'); } catch {/* ignore */}
                  const bundle = {
                    exported_at: new Date().toISOString(),
                    cases: cases.cases, promises: promises.promises,
                    decisions: decisions.decisions, audit: audit.entries, search_presets: presets,
                  };
                  const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `ai-outlook-export-${new Date().toISOString().slice(0, 10)}.json`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                } catch (e) {
                  notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Export failed' });
                }
              }}>
              Download bundle
            </Button>
          </Stack>
        </Card>

        {/* 4. Audit export */}
        <Card padding="sm" radius="sm" withBorder>
          <Stack gap="xs">
            <Group gap="xs">
              <IconHistory size={14} />
              <Text fw={600} size="sm">Audit export</Text>
            </Group>
            <Text size="xs" c="dimmed">
              Hash-chained log of every settings change, rule activation, retention sweep, sign-in.
              Export captures the entire chain — verifiable end-to-end.
            </Text>
            <Group gap="xs">
              <Button size="xs" variant="default" leftSection={<IconFileText size={12} />} onClick={() => downloadAudit('csv')}>
                Download CSV
              </Button>
              <Button size="xs" variant="default" leftSection={<IconFileText size={12} />} onClick={() => downloadAudit('json')}>
                Download JSON
              </Button>
            </Group>
          </Stack>
        </Card>

        <Group justify="flex-end">
          <Button size="sm" loading={saving} onClick={save}
            leftSection={<IconCheck size={14} />}>
            Save Trust Center settings
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

// =========================================================================
// Email aliases (Round 43) — let the user declare every address that's
// "ours" so SLA / closeout / contradictions know which mail is internal.
// =========================================================================

export function EmailAliasesCard(): React.JSX.Element {
  const [primary, setPrimary] = useState('');
  const [aliasesText, setAliasesText] = useState('');
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setBusy(true);
    api.addinEmailAliasesGet()
      .then((r) => {
        setPrimary(r.primary);
        // primary is auto-included server-side; show only extras to edit.
        const extras = r.aliases.filter((a) => a.toLowerCase() !== r.primary.toLowerCase());
        setAliasesText(extras.join('\n'));
      })
      .catch(() => {})
      .finally(() => setBusy(false));
  }, []);

  async function save() {
    setSaving(true);
    try {
      const aliases = aliasesText
        .split(/[\n,;]/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0 && s.includes('@'));
      const r = await api.addinEmailAliasesSet(aliases);
      notifications.show({ message: `Saved ${r.count} alias${r.count === 1 ? '' : 'es'}.` });
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed' });
    } finally { setSaving(false); }
  }

  if (busy) return <Card padding="md" withBorder><Center p="md"><Loader size="sm" /></Center></Card>;

  return (
    <Card padding="md" withBorder>
      <Stack gap="sm">
        <Group gap="xs">
          <ThemeIcon variant="light" color="indigo">
            <IconUser {...ICON_PROPS} />
          </ThemeIcon>
          <Stack gap={0}>
            <Title order={5}>My email addresses</Title>
            <Text size="xs" c="dimmed">
              Tell the AI which addresses belong to you (your aliases + employee mailboxes you're CC'd on).
              SLA, closeout, contradictions, and "from us vs from a customer" all use this list.
            </Text>
          </Stack>
        </Group>
        <Group gap="xs">
          <Badge color="blue" variant="light">Primary: {primary || '(unknown)'}</Badge>
        </Group>
        <Textarea
          label="Additional addresses (one per line)"
          description="e.g. ahmad@broadnet.me, hassan@broadnet.me, support@broadnet.me, admin@broadnet.me"
          autosize minRows={3} maxRows={8}
          value={aliasesText}
          onChange={(e) => setAliasesText(e.currentTarget.value)}
          placeholder={'ahmad@broadnet.me\nhassan@broadnet.me\nsupport@broadnet.me'}
          styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
        />
        <Group justify="flex-end">
          <Button size="xs" loading={saving} onClick={save}>Save aliases</Button>
        </Group>
      </Stack>
    </Card>
  );
}

// =========================================================================
// Private mode (Round 41 #30) — toggle local Ollama for sensitive content.
// Round 43: user pointed out airouter.broadnet.app already routes through
// their infrastructure, so this card is now an advanced opt-in (collapsed).
// =========================================================================

export function PrivateModeCard(): React.JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinSettingsGet>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<Awaited<ReturnType<typeof api.addinSettingsTestOllama>> | null>(null);
  // Round 43: collapsed by default — airouter.broadnet.app is the right
  // primary path; this is for advanced users who want a fully on-box LLM.
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setBusy(true);
    api.addinSettingsGet().then(setData).finally(() => setBusy(false));
  }, []);

  async function save() {
    if (!data) return;
    setSaving(true);
    try {
      await api.addinSettingsSet({
        private_mode: data.private_mode === 1,
        private_model: data.private_model,
        ollama_base_url: data.ollama_base_url,
      });
      notifications.show({
        message: data.private_mode === 1
          ? 'Private mode ON — LLM calls will route to local Ollama.'
          : 'Private mode OFF — using the configured cloud provider.',
      });
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed' });
    } finally { setSaving(false); }
  }

  async function test() {
    if (!data) return;
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.addinSettingsTestOllama({
        base_url: data.ollama_base_url,
        model: data.private_model,
      });
      setTestResult(r);
    } catch (e) {
      setTestResult({ ok: false, error: e instanceof Error ? e.message : 'Failed' });
    } finally { setTesting(false); }
  }

  if (busy && !data) return <Card padding="md" withBorder><Center p="md"><Loader size="sm" /></Center></Card>;
  if (!data) return <></>;

  return (
    <Card padding="md" withBorder>
      <Stack gap="sm">
        <Group gap="xs" justify="space-between">
          <Group gap="xs">
            <ThemeIcon variant="light" color="violet">
              <IconShield {...ICON_PROPS} />
            </ThemeIcon>
            <Stack gap={0}>
              <Title order={5}>AI provider routing</Title>
              <Text size="xs" c="dimmed">
                Default: <code>airouter.broadnet.app</code> (your gateway, multi-provider).
                Advanced: route through a local Ollama instance instead.
              </Text>
            </Stack>
          </Group>
          <Button size="compact-xs" variant="subtle" onClick={() => setOpen((v) => !v)}>
            {open ? 'Hide advanced' : 'Show advanced'}
          </Button>
        </Group>

        <Collapse in={open}>
          <Stack gap="sm" mt="xs">
            <Alert color="gray" variant="light" icon={<IconShield size={14} />}>
              You normally don't need to touch this — airouter.broadnet.app already routes through your own infrastructure. Use this only if you want to skip the gateway entirely and run a model on the same VPS.
            </Alert>
            <Switch
              checked={data.private_mode === 1}
              onChange={(e) => setData({ ...data, private_mode: e.currentTarget.checked ? 1 : 0 })}
              label="Use local Ollama instead of airouter for AI calls"
            />
            <TextInput
              size="xs"
              label="Ollama base URL"
              value={data.ollama_base_url}
              onChange={(e) => setData({ ...data, ollama_base_url: e.currentTarget.value })}
            />
            <TextInput
              size="xs"
              label="Model"
              description="Must be pulled on the box (e.g. qwen2.5:0.5b, llama3.2:1b, mistral)"
              value={data.private_model}
              onChange={(e) => setData({ ...data, private_model: e.currentTarget.value })}
            />
            <Group gap="xs">
              <Button size="xs" loading={saving} onClick={save}>Save</Button>
              <Button size="xs" variant="default" loading={testing} onClick={test}>Test Ollama</Button>
            </Group>
            {testResult && testResult.ok && (
              <Alert color="green" variant="light">
                ✓ Connected. Latency {testResult.latency_ms}ms. Sample: "{testResult.sample}"
                {testResult.models && testResult.models.length > 0 && (
                  <Text size="xs" mt={4}>
                    Available: {testResult.models.map((m) => `${m.name} (${m.size_mb}MB)`).join(', ')}
                  </Text>
                )}
              </Alert>
            )}
            {testResult && !testResult.ok && (
              <Alert color="red" variant="light">
                ✗ Failed: {testResult.error}
                <Text size="xs" mt={4}>
                  Make sure Ollama is running (`ollama serve`) and the model is pulled (`ollama pull {data.private_model}`).
                </Text>
              </Alert>
            )}
          </Stack>
        </Collapse>
      </Stack>
    </Card>
  );
}
